import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Space, Spin, Typography, message } from 'antd';
import { QrcodeOutlined, ReloadOutlined, WechatOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

const rawApiBase = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:4100');
const normalizedApiBase = rawApiBase.replace(/\/+$/, '');
const API_URL = normalizedApiBase.endsWith('/api/v2')
  ? normalizedApiBase
  : normalizedApiBase.endsWith('/api')
    ? `${normalizedApiBase}/v2`
    : `${normalizedApiBase}/api/v2`;
const WECHAT_AUTH_API = `${API_URL}/auth/wechat`;

function pickQrResponse(payload) {
  const data = payload?.data || {};
  return {
    state: payload?.state || data.state,
    qrPayload: payload?.qrPayload || payload?.qr_payload || data.qrPayload || data.qr_payload,
    qrCodeUrl: payload?.qrCodeUrl || payload?.qr_code_url || data.qrCodeUrl || data.qr_code_url
  };
}

const WeChatQRLogin = ({ onSuccess, onError, mode = 'login' }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const pollingIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback((sessionState) => {
    stopPolling();
    pollingIntervalRef.current = setInterval(async () => {
      if (!mountedRef.current) {
        stopPolling();
        return;
      }

      try {
        const response = await axios.get(`${WECHAT_AUTH_API}/status`, {
          params: { state: sessionState }
        });
        const nextStatus = response.data?.status || 'pending';
        setStatus(nextStatus);

        if (nextStatus === 'success' && response.data?.user) {
          stopPolling();
          message.success(mode === 'register' ? '微信身份验证成功' : '微信扫码登录成功');
          onSuccess?.(response.data.user);
        } else if (nextStatus === 'expired') {
          stopPolling();
          setErrorMessage('二维码已过期，请重新获取');
        }
      } catch (error) {
        console.error('检查微信扫码登录状态失败:', error);
      }
    }, 2000);
  }, [mode, onSuccess, stopPolling]);

  const generateQRCode = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      setStatus('pending');
      stopPolling();

      const response = await axios.post(`${WECHAT_AUTH_API}/qrcode`, {
        type: mode === 'register' ? 'register_login' : 'pc_login',
        redirect_uri: `${window.location.origin}${mode === 'register' ? '/register' : '/login'}`
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || '生成二维码失败');
      }

      const qr = pickQrResponse(response.data);
      if (!qr.state) {
        throw new Error('服务器没有返回扫码登录状态标识');
      }
      if (!qr.qrPayload && !qr.qrCodeUrl) {
        throw new Error('服务器没有返回二维码内容');
      }

      setState(qr.state);
      setQrCodeUrl(qr.qrCodeUrl);
      startPolling(qr.state);
    } catch (error) {
      console.error('生成微信扫码登录二维码失败:', error);
      setQrCodeUrl(null);
      setStatus('error');
      setErrorMessage(error.response?.data?.message || error.message || '生成二维码失败');
      onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [mode, onError, startPolling, stopPolling]);

  useEffect(() => {
    mountedRef.current = true;
    generateQRCode();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [generateQRCode, stopPolling]);

  const handleRefresh = () => {
    generateQRCode();
  };

  const renderStatusText = () => {
    if (status === 'scanned') {
      return (
        <Space direction="vertical" align="center">
          <WechatOutlined style={{ fontSize: 24, color: '#07c160' }} />
          <Text style={{ color: '#07c160' }}>扫码成功，请在手机上确认登录</Text>
        </Space>
      );
    }

    if (status === 'expired') {
      return (
        <Space direction="vertical" align="center">
          <Text type="danger">二维码已过期</Text>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新二维码
          </Button>
        </Space>
      );
    }

    if (status === 'error') {
      return (
        <Space direction="vertical" align="center">
          <Text type="danger">{errorMessage}</Text>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh}>
            重试
          </Button>
        </Space>
      );
    }

    return (
      <Space direction="vertical" align="center">
        <WechatOutlined style={{ fontSize: 24, color: '#07c160' }} />
        <Text>请使用「教室小喇叭」微信小程序扫码登录</Text>
        {state && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            二维码 5 分钟内有效
          </Text>
        )}
      </Space>
    );
  };

  return (
    <Card
      style={{
        width: '100%',
        maxWidth: 720,
        textAlign: 'center',
        margin: '0 auto'
      }}
      bordered={false}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            width: 220,
            height: 220,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #e8e8e8',
            borderRadius: 8,
            backgroundColor: '#fafafa',
            position: 'relative'
          }}
        >
          {loading ? (
            <Spin tip="生成中..." />
          ) : qrCodeUrl && status !== 'expired' && status !== 'error' ? (
            <>
              <img
                src={qrCodeUrl}
                alt="微信扫码登录二维码"
                style={{
                  width: 200,
                  height: 200,
                  display: 'block'
                }}
              />
              {status === 'scanned' && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(7, 193, 96, 0.1)',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <div
                    style={{
                      backgroundColor: 'rgba(7, 193, 96, 0.95)',
                      color: '#fff',
                      padding: '8px 16px',
                      borderRadius: 4,
                      fontWeight: 700
                    }}
                  >
                    已扫码
                  </div>
                </div>
              )}
            </>
          ) : (
            <QrcodeOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
          )}
        </div>

        {renderStatusText()}

        {status === 'pending' && qrCodeUrl && (
          <Button type="link" icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新二维码
          </Button>
        )}

        <div
          style={{
            marginTop: 12,
            width: '100%',
            maxWidth: 680,
            marginLeft: 'auto',
            marginRight: 'auto',
            padding: '0 8px',
            boxSizing: 'border-box'
          }}
        >
          <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.65, display: 'block' }}>
            {mode === 'register'
              ? '使用「教室小喇叭」微信小程序扫码验证身份。验证成功后，微信会与本页选择的学校或机构关联。'
              : '使用已在「教室小喇叭」微信小程序注册的账号扫码登录，登录后可进行学生管理、班级管理、广播管理和查看通讯录。'}
          </Text>
        </div>
      </Space>
    </Card>
  );
};

export default WeChatQRLogin;
